import React, { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader, Section, EmptyState } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatInt } from "@/lib/menu-utils";
import { useAuth } from "@/context/AuthContext";

const emptyForm = { name: "", meals_per_day: 100, contact: "", notes: "", active: true };

export default function Companies() {
  const { can } = useAuth();
  const canWrite = can("companies:write");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/companies");
      setRows(data);
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({ name: r.name, meals_per_day: r.meals_per_day, contact: r.contact || "", notes: r.notes || "", active: r.active });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, meals_per_day: Number(form.meals_per_day) };
      if (editingId) await api.put(`/companies/${editingId}`, payload);
      else await api.post("/companies", payload);
      toast.success(editingId ? "Empresa atualizada" : "Empresa cadastrada");
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/companies/${deleteId}`);
      toast.success("Empresa removida");
      setDeleteId(null); load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div data-testid="companies-page">
      <PageHeader
        title="Empresas atendidas"
        subtitle="Cadastro das empresas com quantidade de refeições servidas por dia."
        actions={
          canWrite ? (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreate} data-testid="add-company-btn" className="bg-emerald-900 hover:bg-emerald-800 text-white">
                  <Plus size={16} className="mr-1" /> Nova empresa
                </Button>
              </DialogTrigger>
            <DialogContent className="bg-white" data-testid="company-dialog">
              <DialogHeader>
                <DialogTitle>{editingId ? "Editar empresa" : "Nova empresa"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>Nome *</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Refeições/dia *</Label>
                    <Input type="number" min={1} required value={form.meals_per_day} onChange={(e) => setForm({ ...form, meals_per_day: e.target.value })} data-testid="company-meals-input" />
                  </div>
                  <div>
                    <Label>Contato</Label>
                    <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="company-contact-input" />
                  </div>
                </div>
                <div>
                  <Label>Observações</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="company-notes-input" />
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="company-active-switch" />
                  <span className="text-sm text-slate-700">Empresa ativa (entra na consolidação de compras)</span>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                  <Button type="submit" data-testid="company-submit-btn" className="bg-emerald-900 hover:bg-emerald-800 text-white">
                    {editingId ? "Salvar" : "Cadastrar"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          ) : null
        }
      />

      <Section>
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Carregando…</div>
        ) : rows.length === 0 ? (
          <EmptyState
            title="Nenhuma empresa cadastrada"
            description="Comece cadastrando as empresas que você atende, com a quantidade média de refeições servidas por dia."
            action={canWrite ? <Button onClick={openCreate} className="bg-emerald-900 hover:bg-emerald-800 text-white"><Plus size={16} className="mr-1"/> Cadastrar empresa</Button> : null}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="mp-table w-full" data-testid="companies-table">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Contato</th>
                  <th className="mp-num">Refeições/dia</th>
                  <th>Status</th>
                  {canWrite && <th className="text-right">Ações</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`company-row-${r.id}`}>
                    <td>
                      <div className="font-medium text-slate-900">{r.name}</div>
                      {r.notes && <div className="text-xs text-slate-500">{r.notes}</div>}
                    </td>
                    <td className="text-slate-600">{r.contact || "—"}</td>
                    <td className="mp-num tabular-nums">{formatInt(r.meals_per_day)}</td>
                    <td>
                      {r.active
                        ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">Ativa</span>
                        : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">Inativa</span>}
                    </td>
                    {canWrite && (
                      <td>
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(r)} data-testid={`edit-company-${r.id}`}><Pencil size={15} /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => setDeleteId(r.id)} data-testid={`delete-company-${r.id}`}><Trash2 size={15} /></Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Remover empresa?</AlertDialogTitle>
            <AlertDialogDescription>Todos os cardápios semanais desta empresa serão removidos.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-company" className="bg-red-600 hover:bg-red-700 text-white">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
